import baseApi from '@core/api'
import type {
    CashRespondData,
    CashRespondResponse,
    DepositInstructionActivateData,
    DepositInstructionActivateResponse,
    DepositInstructionCancelData,
    DepositInstructionCancelResponse,
    DepositInstructionCompleteData,
    DepositInstructionCompleteResponse,
    DepositInstructionDetailsData,
    DepositInstructionDetailsResponse,
    DepositInstructionInitData,
    DepositInstructionInitResponse,
    DepositInstructionListData,
    DepositInstructionListResponse,
    DepositInstructionUpdateData,
    DepositInstructionUpdateResponse,
    InstallmentInfoData,
    InstallmentInfoResponse,
    Mark3DClosedData,
    Mark3DClosedResponse,
    MasterPassFailData,
    MasterPassFailResponse,
    MasterPassGetTokenResponse,
    MasterPassInitData,
    MasterPassInitDirectData,
    MasterPassInitDirectResponse,
    MasterPassInitResponse,
    MasterPassMakeData,
    MasterPassMakeResponse,
    MasterPassMarkOTPData,
    MasterPassMarkOTPResponse,
    MasterPassSelectData,
    MasterPassSelectResponse,
    PointDepositEarnableData,
    PointDepositEarnableResponse,
    RefundDepositMakeData,
    RefundDepositMakeResponse,
    RefundDepositSummaryData,
    RefundDepositSummaryResponse,
    UserCardListResponse,
} from './DepositApi.types'

const depositApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        userCardList: builder.query<UserCardListResponse, undefined>({
            query: () => ({
                url: '/user/card/list',
                method: 'GET',
            }),
            providesTags: ['CardList'],
        }),
        masterpassGetToken: builder.query<
            MasterPassGetTokenResponse,
            undefined
        >({
            query: () => ({
                url: '/user/card/masterpass/get-token',
                method: 'GET',
            }),
        }),
        masterpassSelect: builder.mutation<
            MasterPassSelectResponse,
            MasterPassSelectData
        >({
            query: data => ({
                url: '/user/card/masterpass/select',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_r, error) =>
                error
                    ? []
                    : [
                          'CardList',
                          'DepositInstructionsList',
                          'DepositInstructionDetail',
                      ],
        }),
        masterpassUnlink: builder.mutation<undefined, undefined>({
            query: () => ({
                url: '/user/card/masterpass/unlink',
                method: 'PUT',
            }),
            invalidatesTags: (_r, error) =>
                error
                    ? []
                    : [
                          'CardList',
                          'DepositInstructionsList',
                          'DepositInstructionDetail',
                      ],
        }),
        masterpassInit: builder.mutation<
            MasterPassInitResponse,
            MasterPassInitData
        >({
            query: data => ({
                url: '/deposit/masterpass/init',
                method: 'POST',
                body: data,
            }),
        }),
        masterpassInitDirect: builder.mutation<
            MasterPassInitDirectResponse,
            MasterPassInitDirectData
        >({
            query: data => ({
                url: '/deposit/masterpass/init-direct',
                method: 'POST',
                body: data,
            }),
        }),
        masterpassMarkOTP: builder.mutation<
            MasterPassMarkOTPResponse,
            MasterPassMarkOTPData
        >({
            query: data => ({
                url: '/deposit/masterpass/mark-otp',
                method: 'PUT',
                body: data,
            }),
        }),
        masterpassMake: builder.mutation<
            MasterPassMakeResponse,
            MasterPassMakeData
        >({
            query: data => ({
                url: '/deposit/masterpass/make',
                method: 'POST',
                body: data,
            }),
        }),
        masterpassFail: builder.mutation<
            MasterPassFailResponse,
            MasterPassFailData
        >({
            query: data => ({
                url: '/deposit/masterpass/fail',
                method: 'PUT',
                body: data,
            }),
        }),
        pointDepositEarnable: builder.mutation<
            PointDepositEarnableResponse,
            PointDepositEarnableData
        >({
            query: data => ({
                url: `/wallet/point/deposit-earnable`,
                method: 'POST',
                body: data,
            }),
        }),
        mark3DClosed: builder.mutation<Mark3DClosedResponse, Mark3DClosedData>({
            query: data => ({
                url: '/deposit/mark-threed-closed',
                method: 'PUT',
                body: data,
            }),
        }),
        installmentInfo: builder.query<
            InstallmentInfoResponse,
            InstallmentInfoData
        >({
            query: data => ({
                url: '/deposit/installment/info',
                method: 'POST',
                body: data,
            }),
        }),
        refundDepositSummary: builder.query<
            RefundDepositSummaryResponse,
            RefundDepositSummaryData
        >({
            query: data => ({
                url: `/deposit/refund/${data.id}/summary`,
                method: 'GET',
            }),
        }),
        refundDepositMake: builder.mutation<
            RefundDepositMakeResponse,
            RefundDepositMakeData
        >({
            query: data => ({
                url: '/deposit/refund/make',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_r, error) =>
                error ? [] : ['BalanceHistoryList', 'WalletDetail'],
        }),
        cashRespond: builder.mutation<CashRespondResponse, CashRespondData>({
            query: data => ({
                url: '/deposit/cash/respond',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: response =>
                response?.data.status !== 'success' ? [] : ['WalletDetail'],
        }),
        depositInstructionsList: builder.query<
            DepositInstructionListResponse,
            DepositInstructionListData
        >({
            query: data => ({
                url: '/deposit/instructions/list',
                method: 'GET',
                params: data,
            }),
            providesTags: ['DepositInstructionsList'],
            serializeQueryArgs: ({
                endpointName,
                // eslint-disable-next-line no-unused-vars
                queryArgs: { page, ...queryArgs },
            }) => {
                return { endpointName, ...queryArgs }
            },
            merge: (currentData, newData, { arg }) => {
                if (!arg.page || arg.page === 1) return newData

                currentData.data.push(...newData.data)
                currentData.meta.paginate = newData.meta.paginate
            },
        }),
        depositInstructionDetails: builder.query<
            DepositInstructionDetailsResponse,
            DepositInstructionDetailsData
        >({
            query: data => ({
                url: `/deposit/instructions/${data.id}`,
                method: 'GET',
            }),
            providesTags: ['DepositInstructionDetail'],
        }),
        depositInstructionInit: builder.mutation<
            DepositInstructionInitResponse,
            DepositInstructionInitData
        >({
            query: data => ({
                url: '/deposit/instructions/init',
                method: 'POST',
                body: data,
            }),
        }),
        depositInstructionComplete: builder.mutation<
            DepositInstructionCompleteResponse,
            DepositInstructionCompleteData
        >({
            query: data => ({
                url: `/deposit/instructions/${data.id}/complete`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_r, error) =>
                error ? [] : ['DepositInstructionsList'],
        }),
        depositInstructionUpdate: builder.mutation<
            DepositInstructionUpdateResponse,
            DepositInstructionUpdateData
        >({
            query: data => ({
                url: `/deposit/instructions/${data.id}/update`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_r, error) =>
                error
                    ? []
                    : ['DepositInstructionsList', 'DepositInstructionDetail'],
        }),
        depositInstructionCancel: builder.mutation<
            DepositInstructionCancelResponse,
            DepositInstructionCancelData
        >({
            query: data => ({
                url: `/deposit/instructions/${data.id}/cancel`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_r, error) =>
                error
                    ? []
                    : ['DepositInstructionsList', 'DepositInstructionDetail'],
        }),
        depositInstructionActivate: builder.mutation<
            DepositInstructionActivateResponse,
            DepositInstructionActivateData
        >({
            query: data => ({
                url: `/deposit/instructions/${data.id}/activate`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_r, error) =>
                error
                    ? []
                    : ['DepositInstructionsList', 'DepositInstructionDetail'],
        }),
    }),
    overrideExisting: true,
})

export default depositApi
